import React from 'react';
import styled from 'styled-components';
import { createSelector } from 'reselect';
import 'moment/dist/locale/ro';
import 'moment/dist/locale/pt';
import 'moment/dist/locale/es';

import { useAppSelector, useAppDispatch, RootState } from '../../../store';
import { DataElementContext } from '@/page-components/common/DataElementContext';
import { processComponentProps } from '@/page-components/utils/processComponentProps';
import { betsSlipLoadMultiBetDay } from '../../../modules/bets/store/actions/betsSlip';
import { formatTotalOddsValue, formatDate, formatOddName } from '@/components/modules/bets/utils/functions';
import {
  EventWithMarkets,
  clearFetchedEventsWithMarkets,
  fetchEventsWithMarkets,
} from '@/modules/bets/utils/eventsFetcher';

import crests from '../../../modules/bets/utils/crests';
import moment from 'moment';

//import './index.scss';

type MultiBetOfTheDateProps = {
  children: any;
  styleText: string;
  className: string;
  properties?: any;
};

const emptyArray: any = [];
const buildMultiBets = (cfg: any, pm: any, language: string) => {
  if (!('dayMultiBets' in cfg)) {
    return emptyArray;
  }

  if (!Array.isArray(cfg.dayMultiBets)) {
    return emptyArray;
  }

  const dayMultiBets = [];

  for (let i = 0; i < cfg.dayMultiBets.length; i++) {
    const dmb = cfg.dayMultiBets[i];
    const n = { ...dmb, endOffer: Infinity };
    n.stakes = JSON.parse(JSON.stringify(dmb.stakes));

    let hasError = false;

    const dates = [];
    let totalOdds = 1;

    for (const s of n.stakes) {
      if (!(s.event_id in pm)) {
        hasError = true;
        break;
      }

      const m = pm[s.event_id];

      n.endOffer = Math.min(n.endOffer, m.matchDateTime);
      dates.push(m.matchDateTime);

      if (!(m.matchBets && Array.isArray(m.matchBets))) {
        hasError = true;
        break;
      }

      let stake = null;

      for (const mb of m.matchBets) {
        for (const mbo of mb.mbOutcomes) {
          if (mbo.idMbo === s.stake_id) {
            stake = { ...mbo };
            stake.idMb = mb.idMb;
            stake.mboDisplayName = formatOddName(mb.idBet, mbo, m, null, language);
            break;
          }
        }
      }

      if (!stake && m.periods?.length) {
        // let's check it in match periods
        for (const period of m.periods) {
          for (const mb of period.matchBets) {
            for (const mbo of mb.mbOutcomes) {
              if (mbo.idMbo === s.stake_id) {
                stake = { ...mbo };
                stake.idMb = mb.idMb;
                stake.mboDisplayName = formatOddName(mb.idBet, mbo, m, period, language);
                break;
              }
            }
          }
        }
      }

      if (!stake) {
        hasError = true;
        break;
      }

      s.stake = stake;
      s.match = m;
      s.matchFormatted = {
        sportName: m.sportName,
        team1LogoUrl: crests(m.team1Name, m, 1),
        team1Name: m.team1Name,
        team2LogoUrl: crests(m.team2Name, m, 2),
        team2Name: m.team2Name,
        matchDateTimeString: formatDate(m.matchDateTime, language),
      };

      totalOdds *= stake.mboOddValue;
    }

    dates.sort((a, b) => a - b);
    const dateTxtArr = [];

    if (dates.length > 0) {
      if (dates[0]) {
        let lang = language;
        if (lang === 'mx') lang = 'es';

        const dt = moment(dates[0]).locale(lang);

        dateTxtArr.push(dt.format('DD MMM'));
      }

      if (dates[dates.length - 1] && dates.length) {
        let lang = language;
        if (lang === 'mx') lang = 'es';
        const ds = moment(dates[dates.length - 1]).locale(lang);
        dateTxtArr.push(ds.format('DD MMM'));
      }
    }

    n.index = n.number;
    n.dates = dateTxtArr.join(' - ');
    n.totalOdds = formatTotalOddsValue(totalOdds, true);
    n.totalOddsBoosted = formatTotalOddsValue(totalOdds * n.express_factor, true);

    if (hasError) {
      continue;
    }

    dayMultiBets.push(n);
  }

  dayMultiBets.sort((a, b) => a.priority - b.priority);

  return dayMultiBets;
};

export const getPrematchMatches = (state: RootState) => state.bets.prematch.matches;
export const getConfig = (state: RootState) => state.bets.config;
export const getLanguage = (state: RootState) => state.application.language;

export const getMultiBets = createSelector([getConfig, getPrematchMatches, getLanguage], buildMultiBets);

const ModuleElementDiv = styled.div<{ $styleText: string }>((props) => props.$styleText);

const MultiBetOfTheDate = (componentProps: MultiBetOfTheDateProps) => {
  let props = componentProps;

  const dataElementContext = React.useContext(DataElementContext);
  [props] = processComponentProps(props, dataElementContext);

  const dispatch = useAppDispatch();
  const dayMultiBets = useAppSelector((state) => getMultiBets(state));
  const isWinnerFun = useAppSelector((state) => state.bets.app.isWinnerFun);
  const prematchBets = useAppSelector(
    (state) => state.bets.betsSlip.tickets?.[state.bets.betsSlip.currentTicket]?.prematch?.selected,
  );
  const liveBets = useAppSelector(
    (state) => state.bets.betsSlip.tickets?.[state.bets.betsSlip.currentTicket]?.live?.selected,
  );

  const doneFetch = React.useRef(false);

  const configMultiBetLoaded = useAppSelector((state) => state.bets.config.dayMultiBetsLoaded);
  const configMultiBet = useAppSelector((state) => state.bets.config.dayMultiBets);
  const prematchMatches: any = useAppSelector((state) => state.bets.prematch.matches);
  const fullPrematchStateLoaded = useAppSelector((state) => state.bets.prematch.fullStateLoaded);
  const stateUpdated: any = useAppSelector((state) => state.bets.prematch.stateUpdated);

  const [selected, setSelected] = React.useState(0);
  const [dialog, setDialog] = React.useState<any>({
    open: false,
    index: -1,
    data: null,
  });

  React.useEffect(() => {
    if (doneFetch.current != stateUpdated && fullPrematchStateLoaded && configMultiBet?.length) {
      doneFetch.current = stateUpdated;

      const eventsToLoad: { [index: string]: EventWithMarkets } = {};
      for (const dmb of configMultiBet) {
        // @ts-ignore
        for (const st of dmb.stakes) {
          let ev: any;
          if (!(st.event_id in eventsToLoad)) {
            ev = eventsToLoad[st.event_id] = {
              id: st.event_id,
              mType: 'prematch',
            };
          } else {
            ev = eventsToLoad[st.event_id];
          }

          if (!st.event_period_id) {
            if (!ev.marketIds) ev.marketIds = [];
            if (ev.marketIds && ev.marketIds.indexOf(st.bet_id) === -1) {
              ev.marketIds.push(st.bet_id);
            }
          } else {
            if (!ev.periods) ev.periods = [];

            const exists = ev.periods?.findIndex((e: any) => e.id === st.event_period_id);
            if (exists > -1) {
              if (ev.periods[exists].marketIds && ev.periods[exists].marketIds.indexOf(st.bet_id) === -1) {
                ev.periods[exists].marketIds.push(st.bet_id);
              }
            } else {
              ev.periods.push({
                id: st.event_period_id,
                marketIds: [st.bet_id],
              });
            }
          }
        }
      }

      const events: any = Object.keys(eventsToLoad);

      if (events.length) {
        clearFetchedEventsWithMarkets(events);
        fetchEventsWithMarkets({ events: Object.values(eventsToLoad) });
      }
    }
  }, [stateUpdated, configMultiBet, prematchMatches, fullPrematchStateLoaded]);

  React.useEffect(() => {
    let setTicket = false;

    if (!isWinnerFun && prematchBets.length && !liveBets.length) {
      const exists: any = {};

      prematchBets.forEach((bet: any) => {
        exists[`${bet.idSport}-${bet.idMatch}-${bet.idBet}-${bet.idMb}-${bet.idMbo}-${bet.idBo}`] = true;
      });

      dayMultiBets.find((multiBet: any) => {
        if (multiBet.stakes.length === prematchBets.length) {
          let isComplete = true;

          multiBet.stakes.forEach((ticket: any) => {
            const key = `${ticket.match.idSport}-${ticket.stake.idMatch}-${ticket.stake.idBet}-${ticket.stake.idMb}-${ticket.stake.idMbo}-${ticket.stake.idBo}`;
            if (!exists[key]) isComplete = false;
          });

          if (isComplete) {
            setSelected(multiBet.number);
            setTicket = true;
            return true;
          }
        }

        return false;
      });
    }

    if (!setTicket) {
      setSelected(0);
    }
  }, [dayMultiBets, isWinnerFun, liveBets, prematchBets]);

  const onMultiBetClick = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      if (e?.currentTarget?.dataset?.index) {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        if (dayMultiBets?.length) {
          const multiBet = dayMultiBets.find((mb: any) => mb.number === index);
          if (multiBet) {
            dispatch(betsSlipLoadMultiBetDay(multiBet));
            setDialog({ open: false, index: -1, data: null });
          }
        }
      }
    },
    [dayMultiBets, dispatch],
  );

  const onShowMore = React.useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e?.stopPropagation();
      if (e?.currentTarget?.dataset?.index) {
        const index = parseInt(e.currentTarget.dataset.index, 10);
        setDialog((v: any) => {
          if (v.open) {
            return { open: false, index: -1, data: null };
          }
          return { open: true, index: index, data: dayMultiBets[index] };
        });
      } else {
        setDialog({ open: false, index: -1, data: null });
      }
    },
    [dayMultiBets],
  );

  const contextValue = React.useMemo(() => {
    return {
      dayMultiBets,
      onMultiBetClick,
      onShowMore,
      dialogShow: dialog.open,
      dialogIndex: dialog.index,
      dialogData: dialog.data,
      selectedNumber: selected,
    };
  }, [dayMultiBets, onMultiBetClick, onShowMore, dialog, selected, isWinnerFun, liveBets, prematchBets]);

  const extraDataProps: any = {};
  if (props?.properties) {
    Object.keys(props?.properties).forEach((key: any) => {
      if (key?.indexOf('data-') === 0) {
        extraDataProps[key] = props?.properties?.[key];
      }
    });
  }

  //console.log('MultiBetOfTheDate[configMultiBet]', configMultiBet);

  if (configMultiBetLoaded && configMultiBet.length === 0) {
    return null;
  }

  if (!props.properties?.showSkeleton && dayMultiBets.length === 0) {
    return null;
  }

  //console.log('MultiBetOfTheDate[contextValue]', contextValue);

  return (
    <ModuleElementDiv className={props.className ?? ''} $styleText={props.styleText} {...extraDataProps}>
      <DataElementContext.Provider value={contextValue}>{componentProps.children}</DataElementContext.Provider>
    </ModuleElementDiv>
  );
};

export default MultiBetOfTheDate;
